import { useEffect, useState } from "react";
import { Users, UserPlus, Loader2, KeyRound, ShieldCheck, Ban, CheckCircle2, RefreshCw, Copy } from "lucide-react";
import api from "@/lib/api";
import { useUser } from "@/context/UserContext";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const ROL_LABEL = { werknemer: "Werknemer", "or-lid": "OR-lid", beheerder: "Beheerder", superbeheerder: "Superbeheerder" };
// PILOTMAATREGEL v2 (2026-07): "or-lid" tijdelijk niet toewijsbaar; pilot kent Werknemer + Beheerder(s).
const ROL_KEUZE = { werknemer: "Werknemer", beheerder: "Beheerder", superbeheerder: "Superbeheerder" };

// Genereert een sterk tijdelijk wachtwoord (14 tekens, met minimaal 1 hoofd-/kleine letter,
// cijfer en leesbaar leesteken). Ambigue tekens (l/1/O/0) worden vermeden.
const genereerWachtwoord = () => {
  const lower = "abcdefghijkmnpqrstuvwxyz";
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const cijfers = "23456789";
  const symbolen = "!@#$%*?-";
  const alle = lower + upper + cijfers + symbolen;
  const rnd = (set) => set[Math.floor(Math.random() * set.length)];
  let pw = [rnd(lower), rnd(upper), rnd(cijfers), rnd(symbolen)];
  for (let i = pw.length; i < 14; i++) pw.push(rnd(alle));
  return pw.sort(() => Math.random() - 0.5).join("");
};

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("nl-NL", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const NieuweGebruiker = ({ onDone, atCap, maxUsers }) => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [naam, setNaam] = useState("");
  const [rol, setRol] = useState("werknemer");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!email || !naam || !pw) { toast.error("Vul alle velden in."); return; }
    setBusy(true);
    try {
      await api.post("/admin/users", { email, naam, rol, wachtwoord: pw });
      toast.success(t("users.created"));
      setOpen(false); setEmail(""); setNaam(""); setRol("werknemer"); setPw("");
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Aanmaken mislukt."); }
    finally { setBusy(false); }
  };

  const genereer = () => { setPw(genereerWachtwoord()); };
  const kopieer = async () => {
    if (!pw) return;
    try { await navigator.clipboard.writeText(pw); toast.success("Wachtwoord gekopieerd naar klembord."); }
    catch { toast.error("Kopiëren mislukt — selecteer en kopieer handmatig."); }
  };

  if (atCap) {
    return (
      <Button data-testid="new-user-btn" disabled className="zk-primary opacity-60 cursor-not-allowed" title={`Pilot-limiet van ${maxUsers} gebruikers bereikt`}>
        <UserPlus className="w-4 h-4 mr-2" />{t("users.new")}
      </Button>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-user-btn" className="zk-primary"><UserPlus className="w-4 h-4 mr-2" />{t("users.new")}</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{t("users.new")}</DialogTitle>
          <DialogDescription>Maak een account aan. De gebruiker logt in met het tijdelijke wachtwoord en wijzigt dit bij eerste gebruik.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-sm">{t("users.name")}</Label><Input data-testid="nu-naam" value={naam} onChange={(e) => setNaam(e.target.value)} /></div>
          <div><Label className="text-sm">{t("users.email")}</Label><Input data-testid="nu-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
          <div><Label className="text-sm">{t("users.role")}</Label>
            <Select value={rol} onValueChange={setRol}>
              <SelectTrigger data-testid="nu-rol"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(ROL_KEUZE).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div>
            <Label className="text-sm">{t("users.temppw")}</Label>
            <div className="flex gap-2">
              <Input data-testid="nu-pw" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="min. 8 tekens" className="font-mono" />
              <Button type="button" data-testid="nu-genereer" onClick={genereer} variant="outline" size="icon" className="shrink-0 border-[#D8BE95]" title="Genereer sterk wachtwoord">
                <RefreshCw className="w-4 h-4 text-[#5B3A22]" />
              </Button>
              <Button type="button" data-testid="nu-kopieer" onClick={kopieer} variant="outline" size="icon" disabled={!pw} className="shrink-0 border-[#D8BE95]" title="Kopieer wachtwoord">
                <Copy className="w-4 h-4 text-[#5B3A22]" />
              </Button>
            </div>
            <p className="text-xs text-slate-400 mt-1">Klik op het pijltje voor een sterk wachtwoord en deel dit veilig met de deelnemer. Bij eerste login wijzigt de gebruiker dit.</p>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="nu-submit" onClick={submit} disabled={busy} className="zk-primary">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}{t("users.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ResetWachtwoord = ({ u }) => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const genereer = () => setPw(genereerWachtwoord());
  const kopieer = async () => {
    if (!pw) return;
    try { await navigator.clipboard.writeText(pw); toast.success("Wachtwoord gekopieerd naar klembord."); }
    catch { toast.error("Kopiëren mislukt — selecteer en kopieer handmatig."); }
  };
  const submit = async () => {
    if (pw.length < 8) { toast.error("Wachtwoord moet minimaal 8 tekens bevatten."); return; }
    setBusy(true);
    try {
      const r = await api.post(`/admin/users/${u.id}/reset-wachtwoord`, { wachtwoord: pw });
      toast.success(r.data.melding || "Wachtwoord opnieuw ingesteld.");
      setOpen(false); setPw("");
    } catch (e) { toast.error(e.response?.data?.detail || "Mislukt."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setPw(""); }}>
      <DialogTrigger asChild>
        <Button data-testid={`reset-pw-${u.id}`} size="sm" variant="outline" className="h-7 border-slate-300">
          <KeyRound className="w-3.5 h-3.5 mr-1" />{t("users.resetpw")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Wachtwoord resetten</DialogTitle>
          <DialogDescription>Stel een nieuw tijdelijk wachtwoord in voor <span className="font-semibold">{u.email}</span>. Deel dit veilig; de gebruiker wijzigt het bij de eerstvolgende login.</DialogDescription>
        </DialogHeader>
        <div>
          <Label className="text-sm">Nieuw tijdelijk wachtwoord</Label>
          <div className="flex gap-2">
            <Input data-testid={`reset-pw-input-${u.id}`} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="min. 8 tekens" className="font-mono" />
            <Button type="button" data-testid={`reset-pw-genereer-${u.id}`} onClick={genereer} variant="outline" size="icon" className="shrink-0 border-[#D8BE95]" title="Genereer sterk wachtwoord">
              <RefreshCw className="w-4 h-4 text-[#5B3A22]" />
            </Button>
            <Button type="button" data-testid={`reset-pw-kopieer-${u.id}`} onClick={kopieer} variant="outline" size="icon" disabled={!pw} className="shrink-0 border-[#D8BE95]" title="Kopieer wachtwoord">
              <Copy className="w-4 h-4 text-[#5B3A22]" />
            </Button>
          </div>
          <p className="text-xs text-slate-400 mt-1">Klik op het pijltje voor een sterk wachtwoord en op het kopieer-icoon om het te delen.</p>
        </div>
        <DialogFooter>
          <Button data-testid={`reset-pw-submit-${u.id}`} onClick={submit} disabled={busy} className="zk-primary">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Opnieuw instellen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Gebruikersbeheer = () => {
  const { user } = useUser();
  const { t } = useLang();
  const [users, setUsers] = useState([]);
  const [maxUsers, setMaxUsers] = useState(5);
  const [loading, setLoading] = useState(true);

  const load = () => { setLoading(true); api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => setUsers([])).finally(() => setLoading(false)); };
  useEffect(() => {
    load();
    api.get("/auth/status").then((r) => { if (r.data?.pilot_max_users) setMaxUsers(r.data.pilot_max_users); }).catch(() => {});
  }, []);

  const actievePilot = users.filter((u) => u.rol !== "superbeheerder" && u.actief).length;
  const beschikbaar = Math.max(0, maxUsers - actievePilot);
  const atCap = actievePilot >= maxUsers;

  const setRol = async (u, rol) => {
    try { await api.patch(`/admin/users/${u.id}/rol`, { rol }); toast.success("Rol gewijzigd."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Mislukt."); }
  };
  const toggle = async (u) => {
    try { await api.patch(`/admin/users/${u.id}/status`, { actief: !u.actief }); toast.success(u.actief ? "Geblokkeerd." : "Gedeblokkeerd."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Mislukt."); }
  };

  return (
    <div className="animate-fade-up" data-testid="gebruikersbeheer-page">
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div>
          <div className="flex items-center gap-2"><Users className="w-6 h-6 text-[#4A3122]" />
            <h1 className="font-heading text-2xl font-bold text-[#2B211A]">{t("users.title")}</h1></div>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">{t("users.subtitle")}</p>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <span data-testid="pilot-bezet-badge"
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${
                atCap ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-[#FBF3E3] text-[#5B3A22] border-[#E4D3B8]"
              }`}>
              <Users className="w-3.5 h-3.5" /> Bezet: {actievePilot}/{maxUsers}
            </span>
            <span data-testid="pilot-beschikbaar-badge"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border bg-emerald-50 text-emerald-800 border-emerald-200">
              <CheckCircle2 className="w-3.5 h-3.5" /> Beschikbaar: {beschikbaar}/{maxUsers}
            </span>
            {atCap && <span className="text-xs text-amber-700 font-medium">Limiet bereikt — deactiveer een account om een plaats vrij te maken.</span>}
          </div>
        </div>
        <NieuweGebruiker onDone={load} atCap={atCap} maxUsers={maxUsers} />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> {t("common.loading")}</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto" data-testid="users-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="px-4 py-3">{t("users.name")}</th>
                <th className="px-4 py-3">{t("users.role")}</th>
                <th className="px-4 py-3">{t("users.status")}</th>
                <th className="px-4 py-3">{t("users.lastlogin")}</th>
                <th className="px-4 py-3 text-right">{t("users.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-slate-50 last:border-0">
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-800">{u.naam}</p>
                    <p className="text-xs text-slate-400">{u.email} · {u.auth_provider}</p>
                  </td>
                  <td className="px-4 py-3">
                    <Select value={u.rol} onValueChange={(v) => setRol(u, v)}>
                      <SelectTrigger data-testid={`role-select-${u.id}`} className="h-8 w-40"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(ROL_KEUZE).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3">
                    {u.actief ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> {t("users.active")}</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700"><Ban className="w-3.5 h-3.5" /> {t("users.inactive")}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{fmt(u.laatste_login)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <ResetWachtwoord u={u} />
                      <Button data-testid={`toggle-status-${u.id}`} onClick={() => toggle(u)} size="sm" variant="outline" disabled={u.id === user?.id}
                        className={`h-7 ${u.actief ? "border-red-200 text-red-700 hover:bg-red-50" : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"}`}>
                        {u.actief ? t("users.block") : t("users.unblock")}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Gebruikersbeheer;
